import { useState } from 'react';
import { useUser } from '../context/UserContext';
import { Gender, CharacterClass } from '../types/user';
import './ProfileSetup.css';

interface SignUpProps {
  /** Called after the account is created and the user is authenticated. */
  onComplete: () => void;
  onBackToLogin: () => void;
}

/**
 * Step 0 of onboarding: create the account only (display name, email, password).
 * Everything about the person — age, gender, photos, lifestyle, interests —
 * is collected by the authenticated profile wizard (WizardContainer) that
 * follows, so people reveal themselves gradually instead of facing one giant
 * form. Placeholder identity fields sent here are overwritten by the wizard.
 */
export function SignUp({ onComplete, onBackToLogin }: SignUpProps) {
  const { register, authError } = useUser();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    displayName.trim().length >= 2 &&
    email.includes('@') &&
    password.length >= 6 &&
    password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    try {
      const ok = await register({
        username: email.split('@')[0],
        displayName: displayName.trim(),
        email: email.trim(),
        password,
        bio: '',
        age: 18,
        gender: 'other' as Gender,
        characterClass: 'tank' as CharacterClass,
        lookingFor: [],
        interests: [],
        preferences: {
          minAge: 18,
          maxAge: 99,
          interests: [],
          showOnlineOnly: false,
        },
      });
      if (ok) {
        onComplete();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="profile-setup">
      <form className="profile-setup-card" onSubmit={handleSubmit} autoComplete="on">
        <div className="setup-header">
          <h1>Create Your Account</h1>
          <p className="hint">Just the basics — you'll build your profile next, one step at a time.</p>
        </div>

        <div className="setup-content">
          <div className="setup-step">
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                name="displayName"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How others will see you"
                minLength={2}
                maxLength={50}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password (min 6 characters)"
                minLength={6}
              />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
              />
              {confirmPassword && password !== confirmPassword && (
                <span className="field-error">Passwords don't match</span>
              )}
            </div>
            {authError && <span className="field-error">{authError}</span>}
          </div>
        </div>

        <div className="setup-actions">
          <button type="button" className="btn-secondary" onClick={onBackToLogin}>
            Back to Login
          </button>
          <button type="submit" className="btn-primary" disabled={!canSubmit || submitting}>
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>
        </div>
      </form>
    </div>
  );
}
